import axios from "axios";
import { toast } from "react-toastify";
import store, { history } from '../redux/store/index';
import { refreshTokenApi } from '../services/authService';

let blockCall = false;
let isRefreshing = false;
let failedQueue = [];

const toastConfig = {
  position: "bottom-center",
  closeButton: false,
  autoClose: 2000,
  style: { width: "auto", borderRadius: 8 }
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const api = axios.create({
  timeout: 60000,
});

api.interceptors.request.use(
  (config) => {
    const isPublic = config.headers?.isPublic;
    const token = store.getState()?.auth?.token;

    if (!isPublic && token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      config.headers["Content-Type"] = "multipart/form-data";
    } else if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    config.headers.Accept = "application/json";
    config.headers["Referrer-Policy"] = "strict-origin-when-cross-origin";
    config.headers.requestFrom = "web";
    config.headers.isPublic = isPublic;

    return config;
  },
  (error) => Promise.reject(error)
);

// RESPONSE
api.interceptors.response.use(
  (response) => {
    blockCall = false;
    return response;
  },
  async (error) => {
    if (!error.response) {
      toast.error("Network error. Please check your internet connection.", toastConfig);
      return Promise.reject(error);
    }

    const status = error.response.status;
    const originalRequest = error.config;
    const isPublic = originalRequest?.headers?.isPublic;
    if (status === 401 && !isPublic && !originalRequest._retry) {
      const isTokenExpired = error.response?.headers?.['x-token-expired'] === 'true';
      if (!isTokenExpired) {
        return Promise.reject(error);
      }
      if (originalRequest.url.includes('/logout')) {
        if (!blockCall) {
          blockCall = true;
          store.dispatch({ type: "auth/forceLogoutRequest" });
        }
        return Promise.reject(error);
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const currentRefreshToken = store.getState()?.auth?.refreshToken;
      const currentAccessToken = store.getState()?.auth?.token;
      if (!currentRefreshToken) {
        isRefreshing = false;
        if (!blockCall) {
          blockCall = true;
          store.dispatch({ type: "auth/forceLogoutRequest" });
        }
        return Promise.reject(error);
      }

      try {
        const res = await refreshTokenApi({
          refreshToken: currentRefreshToken,
          token: currentAccessToken
        });

        if (res.response_type === "success") {
          const { token, refreshToken } = res.response.data;
          store.dispatch({
            type: "auth/tokenRefreshed",
            payload: { token, refreshToken }
          });
          processQueue(null, token);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } else {
          processQueue(new Error('Refresh failed'), null);
          if (!blockCall) {
            blockCall = true;
            store.dispatch({ type: "auth/forceLogoutRequest" });
          }
          return Promise.reject(error);
        }

      } catch (refreshError) {
        processQueue(refreshError, null);
        if (!blockCall) {
          blockCall = true;
          store.dispatch({ type: "auth/forceLogoutRequest" });
        }
        return Promise.reject(refreshError);

      } finally {
        isRefreshing = false;
      }
    }
    switch (status) {
      case 403:
        if (!blockCall) {
          toast.error(error?.response?.data?.message || "Access Denied", toastConfig);
          blockCall = true;
        }
        break;
      case 500:
        toast.error(error?.response?.data?.message || "Server Error", toastConfig);
        break;
      case 400:
        toast.error(error?.response?.data?.message || "Bad Request", toastConfig);
        break;
      default:
        if (status !== 401) {
          toast.error(error?.response?.data?.message || "Unexpected Error", toastConfig);
        }
    }

    return Promise.reject(error);
  }
);

export default api;